<!--

 -->
<template>

  <FormSelectField label="Where did you stay last night?"
  v-model="answers.livingPlace" stacked @change="onChange"
  :options="listToObject([
    'Place not meant for habitation',
    'Emergency shelter, including hotel or motel paid for with emergency shelter voucher, Host Home shelter',
    'Save Haven',
    'Foster care home or foster care group home',
    'Hospital or other residential non-psychiatric medical facility',
    'Jail, prison, or juvenile detention facility',
    'Long-term care facility or nursing home',
    'Psychiatric hospital or other psychiatric facility',
    'Substance abuse treatment facility or detox center',
    'Transitional housing for homeless persons (including homeless youth)',
    'Residential project or halfway house with no homeless criteria',
    'Hotel or motel paid for without emergency shelter voucher',
    'Host Home (non-crisis)',
    'Staying or living in a friend\'s room, apartment or house',
    'Rental by client, no ongoing housing subsidy',
    'Rental by client, with ongoing housing subsidy',
    'Owned by client, with ongoing housing subsidy',
    'Owned by client, no ongoing housing subsidy',
    'Client doesn\'t know',
    'Client prefers not to answer',
  ])"/>

<FormSelectField class="mt-5" label="How long have you been staying there?"
  v-model="answers.livingDuration" stacked @change="onChange"
  :options="listToObject([
    'One night or less',
    'Two to six nights',
    'One week or more, but less than one month',
    'One month or more, but less than 90 days',
    '90 days or more, but less than one year',
    'One year or longer',
    'Client doesn\'t know',
    'Client prefers not to answer',
  ])"/>

<FormRadioGroupField class="mt-5" label="When did your homelessness start?"
  v-model="answers.homelessnessStart.status" stacked @change="onChange"
  :options="listToObject([
    'Enter date',
    'Client doesn\'t know',
    'Client prefers not to answer',
  ])">
    <template v-slot:first-option>
      <FormTextField v-model="answers.homelessnessStart.dateStarted" autoFocus @change="onChange"/>
    </template>
  </FormRadioGroupField>

  <FormSelectField class="mt-5" label="How many times have you stayed on the street, or in a shelter, in the past 3 years?"
    v-model="answers.timesOnTheStreet" stacked @change="onChange"
    :options="listToObject([
      'One time',
      'Two times',
      'Three times',
      'Four or more times',
      'Client doesn\'t know',
      'Client prefers not to answer',
    ])"/>

<FormSelectField class="mt-5" label="For how many months in total have you been on the street in the past 3 years?"
    v-model="answers.monthsOnTheStreet" stacked @change="onChange"
    :options="listToObject([
			'1 month',
			'2 months',
			'3 months',
			'4 months',
			'5 months',
			'6 months',
			'7 months',
			'8 months',
			'9 months',
			'10 months',
			'11 months',
			'12 months or more',
      'Client doesn\'t know',
      'Client prefers not to answer',
    ])"/>

  <FormCheckboxGroupField class="mt-5" label="Do you receive health insurance from any of the following?"
    v-model="answers.healthInsurance" stacked @change="onChange"
    :options="listToObject([
			'Private - Individual',
			'Private - Employer',
			'Medicare',
			'Medicaid',
			'S-CHIP',
			'VHA',
			'State Funded',
			'Combined Children\'s Health Insurance / Medicaid Program',
			'IHS',
			'COBRA',
			'Wellcare',
			'None of the above',
      'Client doesn\'t know',
      'Client prefers not to answer',
    ])"/>

  <h2 class="mb-2 mt-5">Do any of the following barriers significantly affect your ability to live independently?</h2>
  <div v-for="(barrier, barrierName) of answers.barriers" :key="barrierName">
    <div class="columns is-mobile">
      <FormCheckboxField :boxLabel="barrierName" v-model="barrier.hasBarrier" @change="onChange"/>
      <FormCheckboxField v-if="barrier.hasBarrier" boxLabel="Still Present" v-model="barrier.stillPresent" @change="onChange"/>
    </div>
  </div>

  <FormRadioGroupField class="mt-5" label="Are you a survivor of domestic violence?"
    v-model="answers.domesticViolence" @change="onChange"
    :options="listToObject([
			'Yes',
      'No',
      'Client doesn\'t know',
      'Client prefers not to answer',
    ])"/>

  <FormRadioGroupField class="mt-5" label="Are you a veteran, or in active military service?"
    v-model="answers.veteran" @change="onChange"
    :options="{
      'Yes': 'YES',
      'No': 'NO',
      'Client doesn\'t know': 'NOT_SURE',
      'Client prefers not to say': 'PREFER_NOT_TO_SAY',
    }"/>

  <FormRadioGroupField class="mt-5" label="Do you currently have monthly income, from any source?"
    v-model="answers.income.status" @change="onChange" :options="{
      'Yes': 'YES',
      'No': 'NO',
      'Client doesn\'t know': 'NOT_SURE',
      'Client prefers not to say': 'PREFER_NOT_TO_SAY',
    }"
  />
  <div v-if="answers.income.status == 'YES'">
    <div v-for="(income, incomeName) of answers.income.categories" :key="incomeName">
      <div class="columns is-mobile">
        <FormCheckboxField :boxLabel="incomeName" v-model="income.hasIncome" @change="onChange"/>
        <FormTextField v-if="income.hasIncome" placeholder="Description" v-model="income.description" @change="onChange" autoFocus/>
        <FormTextField v-if="income.hasIncome" placeholder="Amount ($)" v-model="income.amount" @change="onChange" numeric />
      </div>

    </div>
    <div class="mt-3 infoBox"><strong>Total Income</strong>: ${{ Math.round(totalIncome*100)/100 }}</div>
  </div>
  
  <FormRadioGroupField class="mt-5" label="Do you receive any non-cash benefits?"
    v-model="answers.benefits.status" @change="onChange"
    :options="listToObject([
      'Yes',
      'No',
      'Client doesn\'t know',
      'Client prefers not to answer',
    ])">
      <template v-slot:first-option>
        <FormTextField placeholder="Description" v-model="answers.benefits.description" autoFocus/>
      </template>
  </FormRadioGroupField>

  <FormRadioGroupField class="mt-5" label="Do you currently have monthly expenses, from any source?" @change="onChange"
    v-model="answers.expenses.status" :options="{
      'Yes': 'YES',
      'No': 'NO',
      'Client doesn\'t know': 'NOT_SURE',
      'Client prefers not to say': 'PREFER_NOT_TO_SAY',
    }"
  />
  <div v-if="answers.expenses.status == 'YES'">
    <div v-for="(expense, expenseName) of answers.expenses.categories" :key="expenseName">
      <div class="columns is-mobile">
        <FormCheckboxField :boxLabel="expenseName" v-model="expense.hasExpense" @change="onChange"/>
        <FormTextField v-if="expense.hasExpense" placeholder="Description" v-model="expense.description" @change="onChange" autoFocus/>
        <FormTextField v-if="expense.hasExpense" placeholder="Amount ($)" v-model="expense.amount" @change="onChange" numeric />
      </div>

    </div>
    <div class="mt-3 infoBox"><strong>Total Expenses</strong>: ${{ Math.round(totalExpenses*100)/100 }}</div>
  </div>

  <FormRadioGroupField class="mt-5" label="Do you consent to share this information?" @change="onChange"
    v-model="answers.consentToShare" :options="{
      'Yes': 'YES',
      'No': 'NO'
    }"
  />
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import FormTextField from "@/components/FormTextField.vue";
import FormRadioGroupField from "@/components/FormRadioGroupField.vue";
import FormCheckboxField from "@/components/FormCheckboxField.vue";
import FormCheckboxGroupField from "@/components/FormCheckboxGroupField.vue";
import { type HmisClassifiers } from "../model";

const props = defineProps<{
  modelValue: Partial<HmisClassifiers>
}>();

const answers = ref<HmisClassifiers>({
    livingPlace: null,
    livingDuration: null,
    homelessnessStart: {
      status: null,
      dateStarted: "",
    },
    timesOnTheStreet: null,
    monthsOnTheStreet: null,
    healthInsurance: [],
    barriers: {
      'Alcohol use':                  {hasBarrier: false, stillPresent: false},
      'Drug use':                     {hasBarrier: false, stillPresent: false},
      'Chronic health condition':     {hasBarrier: false, stillPresent: false},
      'Physical disability':          {hasBarrier: false, stillPresent: false},
      'Developmental disability':     {hasBarrier: false, stillPresent: false},
      'HIV / AIDS':                   {hasBarrier: false, stillPresent: false},
      'Mental health':                {hasBarrier: false, stillPresent: false},
    },
    domesticViolence: null,
    veteran: null,
    income: {
      status: null,
      categories: {
        'Earned income (i.e. employment income)': {hasIncome: false, description: "", amount: ""},
        'Unemployment insurance':                 {hasIncome: false, description: "", amount: ""},
        'Supplemental Security Income (SSI)':     {hasIncome: false, description: "", amount: ""},
        'Social Security Disability Insurance':   {hasIncome: false, description: "", amount: ""},
        'Veteran\'s Disability Payment':          {hasIncome: false, description: "", amount: ""},
        'Private Disability Insurance':           {hasIncome: false, description: "", amount: ""},
        'Worker\'s Compensation':                 {hasIncome: false, description: "", amount: ""},
        'TANF':                                   {hasIncome: false, description: "", amount: ""},
        'General Assistance':                     {hasIncome: false, description: "", amount: ""},
        'Retirement income from Social Security': {hasIncome: false, description: "", amount: ""},
        'Veteran\'s Pension':                     {hasIncome: false, description: "", amount: ""},
        'Other Pension':                          {hasIncome: false, description: "", amount: ""},
        'Child Support':                          {hasIncome: false, description: "", amount: ""},
        'Alimony or other spousal support':       {hasIncome: false, description: "", amount: ""},
        'Other income':                           {hasIncome: false, description: "", amount: ""},
      }
    },
    benefits: {
      status: null,
      description: "",
    },
    expenses: {
      status: null,
      categories: {
        'Out-of-Pocket Medical Expenses': {hasExpense: false, description: "", amount: ""},
        'Rent':                           {hasExpense: false, description: "", amount: ""},
        'Security Deposit':               {hasExpense: false, description: "", amount: ""},
        'Mortgage':                       {hasExpense: false, description: "", amount: ""},
        'Utilities - Electric':           {hasExpense: false, description: "", amount: ""},
        'Utilities - Gas':                {hasExpense: false, description: "", amount: ""},
        'Utilities - Water':              {hasExpense: false, description: "", amount: ""},
        'Maintenance':                    {hasExpense: false, description: "", amount: ""},
        'Car Payment':                    {hasExpense: false, description: "", amount: ""},
        'Car Insurance':                  {hasExpense: false, description: "", amount: ""},
        'Groceries':                      {hasExpense: false, description: "", amount: ""},
        'Gasoline':                       {hasExpense: false, description: "", amount: ""},
        'Miscellaneous':                  {hasExpense: false, description: "", amount: ""},
      }
    },
    consentToShare: null
});

onMounted(() => {
  // Clone model (we can't mutate it directly)
  console.log("Mounted", props.modelValue);
    answers.value = {...answers.value, ...props.modelValue};
  }
);

const emit = defineEmits(['update:modelValue']);

const onChange = () => {
  console.log("CHANGE", answers);
  emit('update:modelValue', {...answers.value});
}

const listToObject = (strings: string[]) => {
  const result:any = {};
  for (let s of strings) {
    result[s] = s;
  }
  return result;
};

const totalIncome = computed(()=>{
  let total = 0;
  for (const income of Object.values(answers.value.income.categories)) {
    if (income.hasIncome && income.amount.trim().length > 0) {
      total += parseFloat(income.amount);
    }
  }
  return total;
});

const totalExpenses = computed(()=>{
  let total = 0;
  for (const expense of Object.values(answers.value.expenses.categories)) {
    if (expense.hasExpense && expense.amount.trim().length > 0) {
      total += parseFloat(expense.amount);
    }
  }
  return total;
})


</script>

<style lang="scss" scoped>
.infoBox {
    background-color: rgba(0,0,0,0.1);
    padding: 1rem;
    border-radius: 0.5rem;
}
</style>
 